/** Base factions with authored battle-end image + music assets (`{slug}-victory.jpg` / `{slug}-loss.mp3`). */
const BASE_FACTION_ASSET_SLUG = Object.freeze({
  Asrah: 'asrah',
  'Remnants of Humanity': 'remnants',
  'Exolot Dominion': 'exolot',
  'League of Shadows': 'league',
  "Ra's Children": 'ra',
});

/** Pure Sentients strategies use dedicated assets (filenames do not match the generic slug pattern). */
const SENTIENTS_FACTION_NAME = 'The Sentients';
const SENTIENTS_BATTLE_END = Object.freeze({
  assetSlug: 'sentient',
  imageVictorySrc: '/images/sentient-victory.jpg',
  imageLossSrc: '/images/sentient-loss.jpg',
  musicVictorySrc: '/music/sentients-victory.mp3',
  musicLossSrc: '/music/sentient-loss.mp3',
});

function buildSentientsBattleEndPresentation(outcome) {
  if (outcome !== 'win' && outcome !== 'loss') {
    throw new Error('buildSentientsBattleEndPresentation: outcome must be "win" or "loss"');
  }
  return {
    imageSrc:
      outcome === 'win' ? SENTIENTS_BATTLE_END.imageVictorySrc : SENTIENTS_BATTLE_END.imageLossSrc,
    musicSrc:
      outcome === 'win' ? SENTIENTS_BATTLE_END.musicVictorySrc : SENTIENTS_BATTLE_END.musicLossSrc,
    assetSlug: SENTIENTS_BATTLE_END.assetSlug,
    componentFaction: SENTIENTS_FACTION_NAME,
    playerFactionName: SENTIENTS_FACTION_NAME,
  };
}

/** Combined factions resolve to one component base faction for both image and music (same roll). */
const COMBINED_FACTION_BATTLE_END_COMPONENTS = Object.freeze({
  'The Conspiracy': ['League of Shadows', 'Asrah'],
  'The Corrupted Core': ['Exolot Dominion', 'Asrah'],
  'The Covenant': ["Ra's Children", 'League of Shadows'],
  'The Divine Syndicate': ["Ra's Children", 'Exolot Dominion'],
  'The Forsaken Pact': ['Remnants of Humanity', 'Asrah'],
  'The Hollow': ["Ra's Children", 'Asrah'],
  'The Iron Alliance': ['Remnants of Humanity', 'Exolot Dominion'],
  'The Last Crusade': ["Ra's Children", 'Remnants of Humanity'],
  'The Shadow Consortium': ['League of Shadows', 'Exolot Dominion'],
  'The Silent Accord': ['Remnants of Humanity', 'League of Shadows'],
});

/**
 * @param {string} factionName Canonical English faction from calculateFactionFromStrategy
 * @param {string|null} [previousComponent] When rotating combined factions, avoid repeating the same component if possible
 * @returns {string} Component base faction name
 */
function pickBattleEndComponentFaction(factionName, previousComponent = null) {
  if (!factionName || typeof factionName !== 'string') {
    throw new Error('pickBattleEndComponentFaction: factionName is required');
  }

  if (factionName === SENTIENTS_FACTION_NAME) {
    return SENTIENTS_FACTION_NAME;
  }

  if (BASE_FACTION_ASSET_SLUG[factionName]) {
    return factionName;
  }

  const components = COMBINED_FACTION_BATTLE_END_COMPONENTS[factionName];
  if (!components || components.length === 0) {
    throw new Error(`pickBattleEndComponentFaction: no battle-end assets for faction ${factionName}`);
  }

  if (previousComponent && components.includes(previousComponent)) {
    const others = components.filter((faction) => faction !== previousComponent);
    if (others.length > 0) {
      return others[Math.floor(Math.random() * others.length)];
    }
  }

  return components[Math.floor(Math.random() * components.length)];
}

/**
 * @param {string} componentFaction
 * @returns {string}
 */
function getBattleEndAssetSlugForComponentFaction(componentFaction) {
  const slug = BASE_FACTION_ASSET_SLUG[componentFaction];
  if (!slug) {
    throw new Error(
      `getBattleEndAssetSlugForComponentFaction: no asset slug for component faction ${componentFaction}`
    );
  }
  return slug;
}

/**
 * @param {Object} gameState
 * @returns {boolean}
 */
function isFactionBattleEndPresentationEligible(gameState) {
  if (gameState?.warEffortBattleType === 'enemy_hq') {
    const winner = gameState.winner;
    const attackerConqueredHq = winner != null && winner !== 'bot' && winner !== 'campaign_bot';
    if (attackerConqueredHq) {
      return false;
    }
  }
  if (gameState?.exploreUnknownNegaraPeacefulWin === true) {
    return false;
  }
  if (gameState?.exploreUnknownRunHomecoming === true) {
    return false;
  }
  if (gameState?.exploreUnknownDarknessLoss === true) {
    return false;
  }
  return true;
}

/**
 * @param {string} factionName Canonical English faction (base or combined)
 * @param {'win'|'loss'} outcome
 * @param {{ assetSlug?: string, componentFaction?: string, previousComponent?: string|null }} [options]
 *   When assetSlug + componentFaction are passed (from BattleUI navigation), AfterBattle reuses the same roll.
 * @returns {{ imageSrc: string, musicSrc: string, assetSlug: string, componentFaction: string, playerFactionName: string }}
 */
function getFactionBattleEndPresentation(factionName, outcome, options = {}) {
  if (!factionName || typeof factionName !== 'string') {
    throw new Error('getFactionBattleEndPresentation: factionName is required');
  }
  if (outcome !== 'win' && outcome !== 'loss') {
    throw new Error('getFactionBattleEndPresentation: outcome must be "win" or "loss"');
  }

  if (factionName === SENTIENTS_FACTION_NAME) {
    return buildSentientsBattleEndPresentation(outcome);
  }

  let { assetSlug, componentFaction, previousComponent = null } = options;

  if (assetSlug === SENTIENTS_BATTLE_END.assetSlug && componentFaction === SENTIENTS_FACTION_NAME) {
    return buildSentientsBattleEndPresentation(outcome);
  }

  if (assetSlug && componentFaction) {
    const expectedSlug = getBattleEndAssetSlugForComponentFaction(componentFaction);
    if (assetSlug !== expectedSlug) {
      throw new Error(
        `getFactionBattleEndPresentation: assetSlug "${assetSlug}" does not match component faction "${componentFaction}" (expected "${expectedSlug}")`
      );
    }
  } else if (assetSlug && !componentFaction) {
    const match = Object.entries(BASE_FACTION_ASSET_SLUG).find(([, slug]) => slug === assetSlug);
    if (!match) {
      throw new Error(`getFactionBattleEndPresentation: unknown assetSlug ${assetSlug}`);
    }
    componentFaction = match[0];
  } else {
    componentFaction = pickBattleEndComponentFaction(factionName, previousComponent);
    assetSlug = getBattleEndAssetSlugForComponentFaction(componentFaction);
  }

  const outcomeKey = outcome === 'win' ? 'victory' : 'loss';
  return {
    imageSrc: `/images/${assetSlug}-${outcomeKey}.jpg`,
    musicSrc: `/music/${assetSlug}-${outcomeKey}.mp3`,
    assetSlug,
    componentFaction,
    playerFactionName: factionName,
  };
}

/**
 * AfterBattle eligibility: mode-specific backgrounds/music take priority over faction assets.
 * Faction presentation also requires a faction source (nav playerFactionName or battle gameState.state).
 * @param {{
 *   isWarEffortHQEnding?: boolean,
 *   isExploreUnknownHomecoming?: boolean,
 *   isExploreUnknownLoss?: boolean,
 *   hasBattleFactionContext?: boolean,
 * }} context
 * @returns {boolean}
 */
function isAfterBattleFactionPresentationEligible(context) {
  if (context.isWarEffortHQEnding) return false;
  if (context.isExploreUnknownHomecoming) return false;
  if (context.isExploreUnknownLoss) return false;
  if (context.hasBattleFactionContext === false) return false;
  return true;
}

module.exports = {
  BASE_FACTION_ASSET_SLUG,
  COMBINED_FACTION_BATTLE_END_COMPONENTS,
  SENTIENTS_FACTION_NAME,
  SENTIENTS_BATTLE_END,
  pickBattleEndComponentFaction,
  getBattleEndAssetSlugForComponentFaction,
  isFactionBattleEndPresentationEligible,
  getFactionBattleEndPresentation,
  isAfterBattleFactionPresentationEligible,
};
